import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Page, TableList, IconButton, Typography, WithInfoIcon, useOverlayContext, PageContentHeader } from 'seagull';
import { Download } from 'seagull/icons';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { handleRequest } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { utils } from '~/utils';
import styles from './AnchorBilling.module.scss';
import AnchorBillingEmptyState from './AnchorBillingEmptyState.svg';
const testPrefix = 'billing';
const title = 'Anchor Billing';
const downloadLoader = 'getDownloadURL';
function AnchorBillingWithData(props) {
    const { serviceInvoices, credit } = props;
    const { api } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { openNewTab } = useIoContext();
    const { columnDefs, headerItems } = useMemo(()=>{
        const tableConfig = [
            {
                columnDef: {
                    width: [
                        100,
                        200
                    ]
                },
                headerItem: {
                    value: 'Invoice number'
                }
            },
            {
                columnDef: {
                    width: [
                        130,
                        300
                    ]
                },
                headerItem: {
                    value: 'Due date'
                }
            },
            {
                columnDef: {
                    width: [
                        150,
                        400
                    ],
                    hAlign: 'end'
                },
                headerItem: {
                    value: 'Amount'
                }
            }
        ];
        return {
            columnDefs: tableConfig.map((c)=>c.columnDef),
            headerItems: tableConfig.map((c)=>c.headerItem)
        };
    }, []);
    function getRowItems(serviceInvoice, index) {
        return [
            {
                value: serviceInvoice.formattedNumber(),
                testId: "".concat(testPrefix, "-row-").concat(index, "-invoice-number")
            },
            {
                value: serviceInvoice.formattedDueDate(),
                testId: "".concat(testPrefix, "-row-").concat(index, "-due-date")
            },
            {
                value: /*#__PURE__*/ _jsx(WithInfoIcon, {
                    showInfo: !!serviceInvoice.invoices().length,
                    infoTestId: "".concat(testPrefix, "-row-").concat(index, "-related-invoices"),
                    info: /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "".concat(testPrefix, "-row-").concat(index, "-related-invoices-tooltip"),
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                skin: "invertedStandard",
                                weight: "bold",
                                children: "Related invoices"
                            }),
                            serviceInvoice.invoices().map((invoice)=>/*#__PURE__*/ _jsx("div", {
                                    children: "Invoice ".concat(invoice.formattedNumber)
                                }, invoice.id))
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(Typography, {
                        ellipsis: true,
                        skin: "secondary",
                        children: utils.formatDollarAmount(serviceInvoice.total())
                    })
                }),
                testId: "".concat(testPrefix, "-row-").concat(index, "-amount")
            }
        ];
    }
    async function openPdf(pdfFile) {
        const { fileName } = pdfFile;
        overlayContext.startLoader(downloadLoader);
        const result = await handleRequest(api.serviceInvoices.getDownloadURL(fileName));
        overlayContext.stopLoader(downloadLoader);
        if (result.success) {
            openNewTab(result.data.url, '_blank');
        }
    }
    return /*#__PURE__*/ _jsx(Page, {
        className: styles.billingDashboard,
        header: title,
        actions: !!credit && /*#__PURE__*/ _jsxs(Typography, {
            weight: "normal",
            children: [
                "Remaining credit: ",
                utils.formatDollarAmount(credit)
            ]
        }),
        testId: "".concat(testPrefix, "-screen"),
        children: serviceInvoices.length > 0 ? /*#__PURE__*/ _jsxs(TableList.Provider, {
            columnDefs: columnDefs,
            hasActionColumn: true,
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsx(TableList.Header, {
                        items: headerItems
                    })
                }),
                serviceInvoices.map((serviceInvoice, index)=>/*#__PURE__*/ _jsx(TableList.Row, {
                        items: getRowItems(serviceInvoice, index),
                        actionColumn: /*#__PURE__*/ _jsx("div", {
                            "data-testid": "".concat(testPrefix, "-row-").concat(index, "-pdf"),
                            children: serviceInvoice.isPdfFileDone() && /*#__PURE__*/ _jsx(IconButton, {
                                testId: "".concat(testPrefix, "-row-").concat(index, "-download"),
                                onClick: ()=>openPdf(serviceInvoice.pdfFile()),
                                children: /*#__PURE__*/ _jsx(Download, {})
                            })
                        })
                    }, serviceInvoice.id()))
            ]
        }) : /*#__PURE__*/ _jsx("div", {
            className: styles.emptyStateWrapper,
            children: /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.title,
                        children: "Anchor fees"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.subTitle,
                        children: "Anchor fees will appear here."
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.subTitle,
                        children: "Fees are charged for invoices over $10."
                    }),
                    /*#__PURE__*/ _jsx(AnchorBillingEmptyState, {})
                ]
            })
        })
    });
}
export default function AnchorBilling() {
    const worksAt = useStore(selectors.user.business);
    const { api } = useStoreActions();
    useSetDocumentTitle(title);
    if (!worksAt) throw makeSentryIgnoredError('no business');
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: ()=>Promise.all([
                api.serviceInvoices.getByBusinessId(worksAt.id),
                api.serviceInvoices.getCredit()
            ]),
        renderChild: (param)=>{
            let { data: [serviceInvoices, credit] } = param;
            return /*#__PURE__*/ _jsx(AnchorBillingWithData, {
                serviceInvoices: serviceInvoices,
                credit: credit,
                worksAt: worksAt
            });
        }
    });
}
